import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, catchError, map, retry, timeout } from 'rxjs';
import { ApiClientConfig } from '../api-client.config';
import { AdvertisingModels } from './api.advertising.model';

const ADVERTISING_PATH = {
  advertising: '/advertising',
  advertises: '/advertising/advertises',
  adsManagement: '/advertising/advertises/management',
  reportCga: '/advertising/advertises/management/report/cga',
  adsInformation: '/advertising/advertises/management/ads/information',
  accountList: '/business-account/account/statistic',
  totalOfAccountList: '/business-account/account/total'
};

export class AdvertisingApi {
  private apiUrl: string = environment.API_URL;

  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getCampaignTree(
    currentTab: string,
    id: string,
    isDeleted = false
  ): Observable<AdvertisingModels.CampaignTreeResponse> {
    const queryParams = new HttpParams()
      .set('tabCurrent', currentTab)
      .set('id', id)
      .set('isDeleted', isDeleted);
    return this.http
      .get<AdvertisingModels.CampaignTreeResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.adsManagement}/campaign/tree`,
        {
          params: queryParams
        }
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getAdvertisingDetails(
    id: string,
    body: AdvertisingModels.AdvertisingQuery | any
  ): Observable<AdvertisingModels.AdvertisingDetailsResponse> {
    const params = new HttpParams().appendAll(body);

    return this.http
      .get<AdvertisingModels.AdvertisingDetailsResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.advertising}/${id}`,
        { params }
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getAdDetails(id: string): Observable<AdvertisingModels.AdDetailsResponse> {
    return this.http
      .get<AdvertisingModels.AdDetailsResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.advertises}/${id}`
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getCgaList(
    type: string,
    requestData: AdvertisingModels.CgaListRequest
  ): Observable<AdvertisingModels.CgaListResponse> {
    let queryParams = new HttpParams()
      .set('pageNum', requestData.pageNum)
      .set('pageSize', requestData.pageSize);
    if (requestData.accountId && requestData.accountId.length) {
      queryParams = queryParams.set('accountId', requestData.accountId);
    }
    if (requestData.groupId && requestData.groupId.length) {
      queryParams = queryParams.set('groupId', requestData.groupId);
    }
    if (requestData.textSearch && requestData.textSearch.length) {
      queryParams = queryParams.set('textSearch', requestData.textSearch);
    }
    if (requestData.status && requestData.status.length) {
      queryParams = queryParams.set('status', requestData.status);
    }
    if (requestData.startTime && requestData.endTime) {
      queryParams = queryParams
        .set('startTime', requestData.startTime)
        .set('endTime', requestData.endTime);
    }
    if (requestData.sort && requestData.sort.length) {
      queryParams = queryParams.set('sort', requestData.sort);
    }
    if (typeof requestData.isDraft === 'boolean') {
      queryParams = queryParams.set('isDraft', requestData.isDraft);
    }
    if (typeof requestData.isDeleted === 'boolean') {
      queryParams = queryParams.set('isDeleted', requestData.isDeleted);
    }
    return this.http
      .get<AdvertisingModels.CampaignTreeResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.adsManagement}/${type}`,
        {
          params: queryParams
        }
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getAdAccountGeneralInfo(
    currentTab: string,
    id: string,
    isDeleted = false
  ): Observable<AdvertisingModels.AdAccountGeneralInfoResponse> {
    const queryParams = new HttpParams()
      .set('tabCurrent', currentTab)
      .set('id', id)
      .set('isDeleted', isDeleted);
    return this.http
      .get<AdvertisingModels.AdAccountGeneralInfoResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.adsInformation}`,
        {
          params: queryParams
        }
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  reportCga(
    currentTab: string,
    id: string,
    note: string,
    status: string
  ): Observable<AdvertisingModels.ReportCgaResponse> {
    const queryParams = new HttpParams().set('tabCurrent', currentTab).set('id', id);
    const body = { note, status };
    return this.http
      .post<AdvertisingModels.ReportCgaResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.reportCga}`,
        body,
        {
          params: queryParams
        }
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getAdAccountList(
    requestData: AdvertisingModels.AccountListRequest
  ): Observable<AdvertisingModels.AdAccountListPagingResponse> {
    let queryParams = new HttpParams()
      .set('accountType', requestData.accountType)
      .set('pageNum', requestData.pageNum)
      .set('pageSize', requestData.pageSize);
    if (requestData.searchText && requestData.searchText.length) {
      queryParams = queryParams.set('searchText', requestData.searchText);
    }
    if (requestData.sort) {
      queryParams = queryParams
        .set('sortField', requestData.sort.sortField)
        .set('sortType', requestData.sort.sortBy);
    }
    return this.http
      .get<AdvertisingModels.AdAccountListPagingResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.accountList}`,
        {
          params: queryParams
        }
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getTotalOfAccountList(): Observable<AdvertisingModels.TotalOfAccountListResponse> {
    return this.http
      .get<AdvertisingModels.TotalOfAccountListResponse>(
        `${this.apiUrl}${ADVERTISING_PATH.totalOfAccountList}`
      )
      .pipe(
        retry(1),
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }
}
